import {
  FloatingPortal,
  type Placement,
  autoPlacement,
  autoUpdate,
  offset,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import cn from "classnames";
import { useId, useState } from "react";
import { Link } from "react-router-dom";

export interface TooltipValueRow {
  label: string;
  value: React.ReactNode;
}

export interface TooltipLink {
  label: string;
  to?: string;
  href?: string;
  onClick?: () => void;
}

export interface TooltipProps {
  text?: string | React.ReactNode;
  /** Bold header row, separated from the body by a divider */
  header?: React.ReactNode;
  /** Right-aligned label/value rows (e.g. "CPU Usage (p95): 35.0% / 12.5%") */
  values?: TooltipValueRow[];
  /** Free-text content rendered below `values` */
  description?: React.ReactNode;
  /** Optional link row, separated from the body by a divider */
  link?: TooltipLink;
  children: React.ReactNode;
  fluid?: boolean;
  compact?: boolean;
  className?: string;
  placement?: Placement;
  theme?: "light" | "dark";
  /**
   * Reference (trigger) wrapper classes. If omitted, defaults depend on `triggerButton`.
   * Pass explicit classes (e.g. `w-full cursor-not-allowed`) to override width/cursor.
   */
  referenceClassName?: string;
  /** Reference is a focusable button; supports click/tap and stronger ARIA */
  triggerButton?: boolean;
  /** Required accessible name when triggerButton is true */
  triggerAriaLabel?: string;
}

const TooltipLinkRow = ({ link }: { link: TooltipLink }) => {
  const className =
    "inline-flex text-indigo-300 hover:text-indigo-200 text-sm font-medium";
  const content = `${link.label} →`;
  if (link.to) {
    return (
      <Link to={link.to} className={className} onClick={link.onClick}>
        {content}
      </Link>
    );
  }
  if (link.href) {
    return (
      <a
        href={link.href}
        className={className}
        onClick={link.onClick}
        target="_blank"
        rel="noreferrer"
      >
        {content}
      </a>
    );
  }
  return (
    <button type="button" className={className} onClick={link.onClick}>
      {content}
    </button>
  );
};

export const Tooltip = ({
  fluid,
  compact,
  children,
  text,
  header,
  values,
  description,
  link,
  className = "",
  placement,
  theme = "dark",
  referenceClassName: referenceClassNameProp,
  triggerButton = false,
  triggerAriaLabel = "Show tooltip",
}: TooltipProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const contentId = useId();

  const { refs, floatingStyles, context } = useFloating({
    placement: placement,
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      autoPlacement({ allowedPlacements: placement && [placement] }),
      offset(10),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, { move: false, handleClose: safePolygon() }),
    useFocus(context),
    useClick(context, { enabled: triggerButton }),
    useDismiss(context),
    useRole(context, { role: "tooltip" }),
  ]);

  const defaultReferenceClassName = triggerButton
    ? "inline-flex items-center justify-center border-0 bg-transparent p-0 font-inherit text-inherit cursor-pointer rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
    : "cursor-pointer w-fit";

  const referenceProps = {
    ref: refs.setReference,
    ...getReferenceProps(),
    className: cn(referenceClassNameProp ?? defaultReferenceClassName),
    ...(triggerButton
      ? {
          type: "button" as const,
          "aria-label": triggerAriaLabel,
          "aria-expanded": isOpen,
          "aria-describedby": isOpen ? contentId : undefined,
        }
      : {}),
  };

  const isStructured =
    header != null || (values && values.length > 0) || link != null;
  const dividerBorder =
    theme === "dark" ? "border-white/10" : "border-black-100";
  const blockPadding = compact ? "px-2 py-1" : "px-3 py-2";

  let sizeClass = "w-96";
  if (fluid) sizeClass = "w-[60vw] md:w-max";
  if (compact) sizeClass = "w-max";

  const contentSizeClass = isStructured
    ? sizeClass
    : cn(blockPadding, sizeClass);

  return (
    <div className={className}>
      {triggerButton ? (
        <button {...referenceProps}>{children}</button>
      ) : (
        <div {...referenceProps}>{children}</div>
      )}
      {isOpen && (
        <FloatingPortal>
          <div
            id={contentId}
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-[1000]"
          >
            <div
              className={cn([
                "shadow",
                "rounded-md",
                "break-normal",
                "text-sm",
                "text-left",
                theme === "dark"
                  ? "bg-black text-white"
                  : "bg-white text-black",
                contentSizeClass,
              ])}
            >
              {isStructured ? (
                <div className="flex flex-col">
                  {header != null && (
                    <div
                      className={cn(
                        blockPadding,
                        "font-semibold border-b",
                        dividerBorder,
                      )}
                    >
                      {header}
                    </div>
                  )}
                  {(values?.length || description != null) && (
                    <div className={cn(blockPadding, "flex flex-col gap-1")}>
                      {values?.map((row) => (
                        <div
                          key={row.label}
                          className="flex items-baseline justify-between gap-4"
                        >
                          <span className="font-semibold whitespace-nowrap">
                            {row.label}
                          </span>
                          <span className="text-right">{row.value}</span>
                        </div>
                      ))}
                      {description != null && (
                        <div className={values?.length ? "pt-1" : undefined}>
                          {description}
                        </div>
                      )}
                    </div>
                  )}
                  {link != null && (
                    <div
                      className={cn(blockPadding, "border-t", dividerBorder)}
                    >
                      <TooltipLinkRow link={link} />
                    </div>
                  )}
                </div>
              ) : (
                text
              )}
            </div>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};
